import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import {
  trackEvent,
  SchedulingEvents,
  getDistinctId,
  setPersonProperties
} from '../../analytics/posthog'
import {
  recordAvailabilityLeadApiV1SchedulingPublicLinkIdAvailabilityLeadPost
} from '../../client'
import type {
  PublicSchedulingLinkResponse,
  SchedulingSlotResponse
} from '../../client'
import {
  API_BASE_URL
} from '../../config'
import type {
  SlotAvailability
} from './schedulingUtils'
import {
  computeSlotAvailability,
  slotHorizon
} from './schedulingUtils'
import type {
  VisitorAvailabilitySession
} from './visitorAvailabilitySession'
import {
  clearVisitorSession,
  loadVisitorSession,
  saveVisitorSession
} from './visitorAvailabilitySession'
import type {
  AvailabilityProvider,
  Horizon,
  VisitorCalendarProvider
} from './visitorCalendarProviders'
import {
  AvailabilityAuthError,
  AvailabilityDeclinedError,
  providerById
} from './visitorCalendarProviders'

// State machine + post-connect pipeline for the visitor-availability overlay.
// The page just renders: this hook owns the OAuth handoff (via the provider
// adapters), the free/busy map, the hide-busy filter, every analytics event,
// the persisted session, and the server-side lead POST.

export interface VisitorAvailabilityState {
  status: 'idle' | 'connecting' | 'connected' | 'error'
  provider: AvailabilityProvider | null
  email: string | null
  // Free/busy per slot id, computed once at connect time from the same slots
  // the page renders — the single source of truth for markers and analytics.
  availability: Map<number, SlotAvailability> | null
}

const IDLE_STATE: VisitorAvailabilityState = {
  status: 'idle',
  provider: null,
  email: null,
  availability: null,
}

// Everything an overlay run needs, resolved once. The click path and the
// restore path have identical preconditions, so a new one (a new config field,
// a third provider) can't be added to one and forgotten in the other.
interface OverlayContext {
  linkId: string
  provider: VisitorCalendarProvider
  clientId: string
  horizon: Horizon
}

function resolveOverlayContext(
  linkId: string | undefined,
  link: PublicSchedulingLinkResponse | null,
  slots: SchedulingSlotResponse[] | null,
  providerId: AvailabilityProvider
): OverlayContext | null {
  const provider = providerById(providerId)
  const config = link?.visitor_availability
  const clientId = config && provider ? provider.clientIdOf(config) : null
  const horizon = slots ? slotHorizon(slots) : null
  if (!linkId || !provider || !clientId || !horizon) return null
  return { linkId, provider, clientId, horizon }
}

export function useVisitorAvailability({
  linkId,
  link,
  slots
}: {
  linkId: string | undefined,
  link: PublicSchedulingLinkResponse | null,
  slots: SchedulingSlotResponse[] | null
}): {
  state: VisitorAvailabilityState
  hideBusy: boolean
  setHideBusy: (hide: boolean) => void
  connect: (provider: AvailabilityProvider) => Promise<void>
  disconnect: () => void
} {
  // Read once per mount: a session found here suppresses the connect strip
  // from the first paint, so a returning visitor never sees it flash.
  const [restorable] = useState(() => loadVisitorSession())
  const [state, setState] = useState<VisitorAvailabilityState>(() => (
    restorable
      ? {
          status: 'connecting',
          provider: restorable.provider,
          email: restorable.email,
          availability: null,
        }
      : IDLE_STATE
  ))
  // Checked by default: a visitor who just handed us their calendar wants the
  // clashes gone, and the filter is inert until one is connected anyway. A
  // restored session brings the visitor's own choice back with it.
  const [hideBusy, setHideBusyState] = useState(restorable?.hideBusy ?? true)
  // Bumped by every event that claims the overlay — a connect click, the
  // restore run, a disconnect — so a slow popup (or a slow restore) resolving
  // after one of the others can tell it lost and drop its result. Still 0
  // means nothing has claimed it yet, which is what gates the restore.
  const connectSeqRef = useRef(0)
  // One lead per connected address per pageload (also guards StrictMode/state
  // races from double-firing the POST). Repeats across pageloads are deduped
  // where the lead actually lives: the event carries a stable per-visitor
  // distinct id, so "one lead per visitor per link" is a query-time count.
  const leadSentForEmailRef = useRef<string | null>(null)
  const setHideBusy = useCallback((hide: boolean) => {
    setHideBusyState(hide)
    // Merged into whatever the store currently holds, never into an in-memory
    // copy: with two links open (this hook's whole point) a Disconnect in one
    // tab clears the key, and writing a remembered session back here would
    // resurrect the grant the visitor just removed.
    const stored = loadVisitorSession()
    if (stored) saveVisitorSession({ ...stored, hideBusy: hide })
  }, [])

  // The one place a grant is discarded, so the click path and the restore path
  // cannot disagree about what a given failure means. Anything short of the
  // provider saying the grant is dead leaves it alone — a flaky read must not
  // cost the visitor a fresh consent popup for a token still good for the hour.
  const discardGrantIfRevoked = useCallback((err: unknown): boolean => {
    const revoked = err instanceof AvailabilityAuthError
    if (revoked) clearVisitorSession()
    return revoked
  }, [])

  // Everything after a token is in hand, shared by the click path and the
  // restore path so a restored overlay marks identically. Returns false when a
  // later connect/disconnect superseded this run, so the caller can skip its
  // own follow-up work.
  const applyBusy = useCallback(async (
    ctx: OverlayContext,
    session: VisitorAvailabilitySession,
    seq: number
  ): Promise<boolean> => {
    const { busyIntervals, truncated } = await ctx.provider.fetchBusy(
      session.accessToken, ctx.horizon
    )
    if (connectSeqRef.current !== seq) return false
    const availability = computeSlotAvailability(slots ?? [], busyIntervals)
    setState({
      status: 'connected',
      provider: ctx.provider.id,
      email: session.email,
      availability,
    })

    setPersonProperties({ email: session.email, name: session.name })
    const statuses = [...availability.values()]
    trackEvent(SchedulingEvents.AVAILABILITY_MARKED, {
      link_id: ctx.linkId,
      provider: ctx.provider.id,
      slot_count: statuses.length,
      busy_count: statuses.filter((s) => s === 'busy').length,
      // A cut-short provider read (Graph page cap) means some slots may be
      // falsely green — practically unreachable, but measurable if it isn't.
      truncated,
    })
    return true
  }, [slots])

  // Server-side lead capture — the durable record (this page's PostHog is
  // cookieless and ad-blockable). Best-effort; never disturbs the page.
  //
  // Deliberately NOT on the restore path: the visitor consented to identify
  // themselves on the link they clicked connect on, and a restored session can
  // land on a different host's link with no click at all. Disclosing them to
  // that owner is not something they agreed to.
  const recordLead = useCallback((
    ctx: OverlayContext,
    session: VisitorAvailabilitySession
  ) => {
    if (leadSentForEmailRef.current === session.email) return
    leadSentForEmailRef.current = session.email
    recordAvailabilityLeadApiV1SchedulingPublicLinkIdAvailabilityLeadPost({
      path: { link_id: ctx.linkId },
      body: {
        email: session.email,
        name: session.name,
        provider: session.provider,
        anon_distinct_id: getDistinctId(),
      },
      baseURL: API_BASE_URL,
    }).catch(() => {})
  }, [])

  const connect = useCallback(async (providerId: AvailabilityProvider) => {
    const ctx = resolveOverlayContext(linkId, link, slots, providerId)
    if (!ctx) return

    trackEvent(SchedulingEvents.AVAILABILITY_CONNECT_CLICKED, {
      link_id: ctx.linkId,
      provider: providerId,
    })
    const seq = ++connectSeqRef.current
    setState((prev) => ({ ...prev, status: 'connecting', provider: providerId }))
    try {
      const auth = await ctx.provider.authorize(ctx.clientId)
      if (connectSeqRef.current !== seq) return
      const session: VisitorAvailabilitySession = {
        provider: providerId,
        email: auth.identity.email,
        name: auth.identity.name,
        accessToken: auth.accessToken,
        expiresAt: auth.expiresAt,
        hideBusy,
      }
      // Written before the free/busy read, so a tab closed while it is in
      // flight still comes back connected rather than re-prompting.
      saveVisitorSession(session)
      trackEvent(SchedulingEvents.AVAILABILITY_CONNECTED, {
        link_id: ctx.linkId,
        provider: providerId,
      })
      if (await applyBusy(ctx, session, seq)) recordLead(ctx, session)
    } catch (err) {
      if (connectSeqRef.current !== seq) return
      discardGrantIfRevoked(err)
      if (err instanceof AvailabilityDeclinedError) {
        setState(IDLE_STATE)
        trackEvent(SchedulingEvents.AVAILABILITY_DECLINED, {
          link_id: ctx.linkId,
          provider: providerId,
        })
        return
      }
      setState({ ...IDLE_STATE, status: 'error', provider: providerId })
      trackEvent(SchedulingEvents.AVAILABILITY_ERROR, {
        link_id: ctx.linkId,
        provider: providerId,
        error_message: err instanceof Error ? err.message : 'Could not read your calendar',
      })
    }
  }, [linkId, link, slots, hideBusy, applyBusy, recordLead, discardGrantIfRevoked])

  // Restore: the token is already granted, so this is a silent free/busy read
  // with no popup. Runs once, as soon as the link config and slots land.
  useEffect(() => {
    if (!restorable || connectSeqRef.current !== 0) return
    // The link and its slots are fetched in parallel; both have to land
    // before a missing one can be read as "this link has no overlay".
    if (!link || slots === null) return
    const ctx = resolveOverlayContext(linkId, link, slots, restorable.provider)
    // The overlay can be off for this link's owner (or missing this provider)
    // even though the visitor connected on another link — fall back to a plain
    // booking page rather than a stuck spinner.
    if (!ctx) {
      connectSeqRef.current += 1
      setState(IDLE_STATE)
      return
    }
    const seq = ++connectSeqRef.current
    trackEvent(SchedulingEvents.AVAILABILITY_RESTORED, {
      link_id: ctx.linkId,
      provider: ctx.provider.id,
    })
    void applyBusy(ctx, restorable, seq).catch((err: unknown) => {
      if (connectSeqRef.current !== seq) return
      // Offer the button again rather than the red error state — the visitor
      // never clicked anything. The grant is only discarded when the provider
      // says it's gone; a flaky read leaves it in place, so the next pageload
      // restores silently instead of charging a fresh consent popup.
      const revoked = discardGrantIfRevoked(err)
      setState(IDLE_STATE)
      // AVAILABILITY_RESTORED fires before the read, so it counts attempts;
      // this is what makes the completed share — and the revoked-grant rate
      // the whole keep-vs-discard split turns on — visible.
      trackEvent(SchedulingEvents.AVAILABILITY_RESTORE_FAILED, {
        link_id: ctx.linkId,
        provider: ctx.provider.id,
        grant_revoked: revoked,
      })
    })
  }, [restorable, linkId, link, slots, applyBusy, discardGrantIfRevoked])

  const disconnect = useCallback(() => {
    connectSeqRef.current += 1
    clearVisitorSession()
    trackEvent(SchedulingEvents.AVAILABILITY_DISCONNECTED, {
      link_id: linkId,
      provider: state.provider,
    })
    setState(IDLE_STATE)
    setHideBusyState(true)
  }, [linkId, state.provider])

  return { state, hideBusy, setHideBusy, connect, disconnect }
}
